import React, { useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { consoleApi } from "../../services/consoleApiService";
import { useConsoleFetch } from "../../hooks/useConsoleFetch";
import ConsoleViewShell from "./ConsoleViewShell";
import { ConsolePageHeader, HelpIcon, SectionLabel, OpsCard, OpsList, GapRow } from "./ConsoleShared";
import {
  CONSOLE_DAYS,
  CONSOLE_DAY_FULL,
  CONSOLE_SLOTS,
  getCityFromSearchParams,
  getConsoleMeetingsRangeFromSearchParams,
  getConsoleRunUrl,
} from "../../utils/consoleUtils";
import "../../styles/console.css";

const HEALTH_CLASSES = ["hc-cov", "hc-frag", "hc-str", "hc-tot", "hc-goal"];

const PILLAR_CONFIG = {
  Coverage: { max: 40, tip: "Measures how many of the 14 weekly AM/PM slots have at least one run." },
  Redundancy: { max: 25, tip: "Slots with 2+ runs. Protects the city if one club stops." },
  Distribution: { max: 20, tip: "Number of distinct neighborhoods runs occur in." },
  Anchors: { max: 15, tip: "Key cultural slots covered. These stabilize the city rhythm." },
};

const DOCTRINE_ROWS = [
  { title: "Coverage first", description: "Filled slots matter more than engagement or noise." },
  { title: "Anchor logic is manual", description: "Never auto-promote a run to anchor status." },
  { title: "Recurring beats one-off", description: "Only weekly and biweekly rhythm should shape city truth." },
];

export default function CoverageView() {
  const [searchParams] = useSearchParams();
  const cityId = getCityFromSearchParams(searchParams);
  const meetingsRange = getConsoleMeetingsRangeFromSearchParams(searchParams);

  const fetcher = useCallback(() => {
    if (!cityId) return Promise.resolve(null);
    return consoleApi.stats.coverage(cityId, { range_key: meetingsRange });
  }, [cityId, meetingsRange]);
  const { data, loading, error } = useConsoleFetch(fetcher, [cityId, meetingsRange]);

  const cityName = data?.city_name ?? "";
  const moderateHealthHint =
    data &&
    typeof data.coverage_score === "number" &&
    data.coverage_score >= 65 &&
    typeof data.anchor_slots_covered === "number" &&
    data.anchor_slots_covered >= 3 &&
    typeof data.total_runs === "number" &&
    data.total_runs >= 20 &&
    data.phase === "seeding";

  return (
    <ConsoleViewShell loading={loading} error={error} loadingLabel="Loading coverage…">
      {!cityId && (
        <div className="console-view">
          <p className="console-muted-text">Select a city to view coverage.</p>
        </div>
      )}
      {cityId && data && (
        <div className="console-main-inner console-coverage">
          <ConsolePageHeader
            label="City Seeding"
            title="Coverage"
            sub="Slot grid, health metrics, and next moves. All data from live coverage API."
          />

          {/* Moderate health hint (v18) */}
          {moderateHealthHint && (
            <div className="console-moderate-health-hint">
              <span className="console-moderate-health-emoji">&#128993;</span>
              <div>
                <div className="console-moderate-health-title">City Health: Moderate</div>
                <div className="console-moderate-health-sub">
                  Coverage ≥ 65% · Anchors ≥ 3 · Runs ≥ 20 — you may begin seeding another city.
                </div>
              </div>
            </div>
          )}

          {/* City Health Banner (v18) */}
          {data.health_banner && (() => {
            const covered = data.health_banner.covered_slots;
            const bannerStats = [
              { label: "Coverage", val: covered != null ? `${covered}/14` : "", sub: "slots filled" },
              { label: "Fragile Slots", val: data.stability_forecast?.fragile_count != null ? String(data.stability_forecast.fragile_count) : "", sub: "single-run slots" },
              { label: "Anchor Slots", val: data.health_banner.anchor_slots_covered != null ? `${data.health_banner.anchor_slots_covered}/6` : "", sub: "key slots covered" },
              { label: "Neighborhoods", val: data.health_banner.neighborhood_spread != null ? String(data.health_banner.neighborhood_spread) : "", sub: "regions active" },
            ];
            return (
              <div className="console-city-health-banner">
                <div className="console-health-banner-left">
                  <div className="console-health-banner-eyebrow">{cityName} · City Status</div>
                  <div className="console-health-banner-phase">
                    {data.phase != null ? data.phase : ""}
                    {typeof covered === "number" && covered >= 12 ? (
                      <span className="console-runnable-pill">Runnable ✓</span>
                    ) : typeof covered === "number" ? (
                      <span className="console-to-runnable-pill">
                        {Math.min(100, Math.round((covered / 12) * 100))}% to runnable
                      </span>
                    ) : null}
                  </div>
                  <div className="console-health-banner-sub">
                    A city is runnable when a traveler can find a run any day.
                  </div>
                </div>
                {bannerStats.map((s) => (
                  <div key={s.label} className="console-health-banner-stat">
                    <div className="console-health-banner-stat-label">{s.label}</div>
                    <div className="console-health-banner-stat-val">{s.val}</div>
                    <div className="console-health-banner-stat-sub">{s.sub}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* City Focus Band (v2) */}
          {(() => {
            const coveredSlots = data.health_banner?.covered_slots != null
              ? data.health_banner.covered_slots
              : (Array.isArray(data.slot_map) ? data.slot_map.filter((s) => typeof s.count === "number" && s.count > 0).length : undefined);
            const focusItems = [
              { label: "Current city", value: cityName },
              { label: "City phase", value: data.phase != null ? data.phase : "" },
              { label: "Coverage", value: coveredSlots != null ? `${coveredSlots}/14` : "" },
              { label: "Fragile slots", value: data.stability_forecast?.fragile_count != null ? String(data.stability_forecast.fragile_count) : "" },
              { label: "Next leverage move", value: data.next_leverage_move != null ? data.next_leverage_move : "" },
            ];
            return (
              <div className="focus-band console-ops-focus-band">
                {focusItems.map((item) => (
                  <div key={item.label} className="focus-band-card console-ops-focus-item">
                    <div className="focus-band-label console-ops-focus-label">{item.label}</div>
                    <div className="focus-band-value console-ops-focus-value">{item.value}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Dev: Computed-Only Metrics (v18) — collapsible */}
          <details className="console-dev-details">
            <summary>Dev: Computed-Only Metrics (not persisted in DB)</summary>
            <div className="console-dev-details-body">
              The following values are <strong>computed at read time</strong> from published run/club records. They are <strong>never stored in the database</strong>:
              <br />
              <span className="console-dev-details-code">
                coverage_score · health_score · gap_finder · anchor_risk · club_completeness
                <br />
                network_scoreboard · traveler_week · submission_counts
              </span>
              <br />
              <strong className="console-dev-details-strong">Backend schema and state transitions are the source of truth.</strong> Console defines UI behavior only.
            </div>
          </details>

          {/* City Rhythm (v18) */}
          {data.city_rhythm && (
            <div className="console-city-rhythm-wrap">
              <SectionLabel
                label="City Rhythm"
                tip="Stats use the Schedule window in the bar above (e.g. Next 7 days). Slot grid, operator tasks, and next-best runs use that window; City integrity uses whether each club has any published run at all."
              />
              <div className="console-city-rhythm-row">
                <div className="console-rhythm-chip">
                  <div className="console-rhythm-chip-label">Most Active Day</div>
                  <div className="console-rhythm-chip-val">
                    {CONSOLE_DAY_FULL[data.city_rhythm.most_active_day] ?? data.city_rhythm.most_active_day}
                  </div>
                  <div className="console-rhythm-chip-sub">
                    {data.slot_map && data.city_rhythm.most_active_day
                      ? data.slot_map
                          .filter((s) => s.day === data.city_rhythm.most_active_day)
                          .reduce((n, s) => n + (typeof s.count === "number" ? s.count : 0), 0)
                      : ""}{" "}
                    runs
                  </div>
                </div>
                <div className="console-rhythm-chip">
                  <div className="console-rhythm-chip-label">Weakest Slot</div>
                  <div className="console-rhythm-chip-val">
                    {data.city_rhythm.weakest_slot
                      ? `${CONSOLE_DAY_FULL[data.city_rhythm.weakest_slot.day] ?? data.city_rhythm.weakest_slot.day} ${data.city_rhythm.weakest_slot.slot != null ? String(data.city_rhythm.weakest_slot.slot).toUpperCase() : ""}`.trim()
                      : null}
                  </div>
                  <div className="console-rhythm-chip-sub">
                    {data.city_rhythm.weakest_slot
                      ? `${data.city_rhythm.weakest_slot.n} run${data.city_rhythm.weakest_slot.n !== 1 ? "s" : ""} — needs backup`
                      : "No slots filled"}
                  </div>
                </div>
                <div className="console-rhythm-chip">
                  <div className="console-rhythm-chip-label">Anchor Runs</div>
                  <div className="console-rhythm-chip-val">{data.city_rhythm.anchor_run_count != null ? data.city_rhythm.anchor_run_count : ""}</div>
                  <div className="console-rhythm-chip-sub">runs tagged anchor</div>
                </div>
                <div className="console-rhythm-chip">
                  <div className="console-rhythm-chip-label">Unverified</div>
                  <div className="console-rhythm-chip-val">{data.city_rhythm.unverified_count != null ? data.city_rhythm.unverified_count : ""}</div>
                  <div className="console-rhythm-chip-sub">
                    {data.city_rhythm.unverified_count === 0 ? "All runs confirmed" : "need confirmation"}
                  </div>
                </div>
                <div className="console-rhythm-chip">
                  <div className="console-rhythm-chip-label">Total Weekly Runs</div>
                  <div className="console-rhythm-chip-val">{data.city_rhythm.total_published != null ? data.city_rhythm.total_published : ""}</div>
                  <div className="console-rhythm-chip-sub">
                    {typeof data.city_rhythm.draft_review_count === "number" && data.city_rhythm.draft_review_count > 0
                      ? `published · ${data.city_rhythm.draft_review_count} draft`
                      : "published · all live"}
                  </div>
                </div>
                {typeof data.city_rhythm.draft_review_count === "number" && data.city_rhythm.draft_review_count > 0 && (
                  <div className="console-rhythm-chip">
                    <div className="console-rhythm-chip-label">Draft / Review</div>
                    <div className="console-rhythm-chip-val">{data.city_rhythm.draft_review_count}</div>
                    <div className="console-rhythm-chip-sub">not counted toward coverage</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Operator tasks — always show */}
          <OpsCard kicker="Today's operator tasks">
            <OpsList
              items={Array.isArray(data.operator_tasks) ? data.operator_tasks : []}
              emptyMessage="No immediate operator tasks."
            />
          </OpsCard>

          {/* City integrity — always show */}
          <OpsCard kicker="City integrity">
            <OpsList
              items={Array.isArray(data.integrity_issues) ? data.integrity_issues : []}
              emptyMessage="City structure is holding. No major structural alarms right now."
            />
          </OpsCard>

          {/* Next Best Runs — always show (gaps = next_best_runs from API) */}
          <SectionLabel label="Next Best Runs to Add" tip="Priority-ranked list of slots to fill. Empty slots first, then fragile." />
          <div className="console-gap-list">
            {Array.isArray(data.next_best_runs) && data.next_best_runs.length > 0
              ? data.next_best_runs.map((g, i) => (
                  <GapRow key={i} day={g.day} slot={g.slot} state={g.state} />
                ))
              : (
                  <div className="console-gap-empty-msg console-gap-all-covered">All slots covered.</div>
                )}
          </div>

          {/* Composite score band (v18) or legacy coverage % */}
          <div className="console-score-band">
            <div className="console-score-left">
              <div className="console-score-city">{cityName}</div>
              {data.city_score_total != null ? (
                <>
                  <div className="console-score-num">
                    {data.city_score_total}
                    <span className="console-score-denom">/100</span>
                  </div>
                  <div className="console-score-lbl">RunCards City Score</div>
                </>
              ) : typeof data.coverage_score === "number" ? (
                <>
                  <div className="console-score-num">
                    {data.coverage_score}
                    <span className="console-score-denom">%</span>
                  </div>
                  <div className="console-score-lbl">Coverage</div>
                </>
              ) : (
                <>
                  <div className="console-score-num">
                    {typeof data.coverage_score === "number" && (
                      <>
                        {data.coverage_score}
                        <span className="console-score-denom">%</span>
                      </>
                    )}
                  </div>
                  <div className="console-score-lbl">Coverage</div>
                </>
              )}
            </div>
            {data.city_score_pillars && (
              <div className="console-score-pillars">
                {Object.entries(data.city_score_pillars)
                  .filter(([name]) => PILLAR_CONFIG[name])
                  .map(([name, val]) => {
                    const config = PILLAR_CONFIG[name];
                    return (
                      <div key={name} className="console-pillar">
                        <div className="console-pillar-name">
                          {name}
                          <HelpIcon tip={config.tip} />
                        </div>
                        <div className="console-pillar-val">
                          {val}<span className="console-pillar-max">/{config.max}</span>
                        </div>
                        <div className="console-pillar-track">
                          <div className="console-pillar-fill" style={{ width: `${(val / config.max) * 100}%` }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Slot Health Engine (v18) — before City Health bar and grid */}
          {data.slot_health && (
            <div className="console-slot-health-engine">
              <div className="console-she-head">
                <span className="console-she-label">Slot Health</span>
                <span className={`console-she-status console-she-${data.slot_health.status_class ?? ""}`}>
                  {data.slot_health.status}
                </span>
              </div>
              <div className="console-she-summary">{data.slot_health.summary}</div>
              <div className="console-she-body">
                <div>
                  <div className="console-she-col-label">Current issues</div>
                  <div className="console-she-issues">
                    {Array.isArray(data.slot_health.issues) && data.slot_health.issues.length > 0
                      ? data.slot_health.issues.map((issue, i) => (
                          <div key={i} className="console-she-issue">
                            {issue}
                          </div>
                        ))
                      : (
                          <div className="console-she-issue console-she-issue-ok">No critical issues detected.</div>
                        )}
                  </div>
                </div>
                <div>
                  <div className="console-she-col-label">Best next move</div>
                  <div className="console-she-move">{data.slot_health.move}</div>
                </div>
              </div>
            </div>
          )}

          <SectionLabel label="City Health" tip="Five quick metrics: Coverage = slots with a run, Fragile = only 1 run in slot, Strong = 2+ runs in slot." />
          <div className="console-health-bar">
            {data.health_bar && data.health_bar.length > 0
              ? data.health_bar.map((h, i) => (
                  <div
                    key={i}
                    className={`console-hc ${HEALTH_CLASSES[i] ?? ""}`}
                  >
                    <div className="console-hc-lbl">{h.label}</div>
                    <div className="console-hc-val">
                      {h.value}
                      {h.pct != null && (
                        <span className="console-hc-pct"> {h.pct}%</span>
                      )}
                    </div>
                    {h.sub && <div className="console-hc-sub">{h.sub}</div>}
                  </div>
                ))
              : null}
          </div>

          <div className="console-two-col">
            <div className="console-grid-panel">
              <SectionLabel label="Coverage Grid" tip="14 slots: AM and PM for each day Mon–Sun. Goal: fill every slot so a traveler can run any day." />
              <div className="console-grid-legend">
                <span className="console-leg-item">
                  <span className="console-leg-dot leg-empty" /> Empty
                </span>
                <span className="console-leg-item">
                  <span className="console-leg-dot leg-fragile" /> Fragile
                </span>
                <span className="console-leg-item">
                  <span className="console-leg-dot leg-stable" /> Stable
                </span>
                <span className="console-leg-item">
                  <span className="console-leg-dot leg-strong" /> Strong
                </span>
              </div>
              <div className="console-cov-grid">
                <div className="console-g-corner" />
                {CONSOLE_DAYS.map((d) => (
                  <div key={d} className="console-g-dh">
                    {d}
                  </div>
                ))}
                {CONSOLE_SLOTS.map((slot) => (
                  <React.Fragment key={slot}>
                    <div className="console-g-rl">
                      <span className="rl-m">{slot.toUpperCase()}</span>
                      <span className="rl-s">
                        {slot === "am" ? "4–10am" : "5–8pm"}
                      </span>
                    </div>
                    {CONSOLE_DAYS.map((day) => {
                      const cell =
                        data.slot_map &&
                        data.slot_map.find(
                          (s) => s.day === day && s.slot === slot
                        );
                      const state = cell?.state;
                      const runs = cell?.runs ?? [];
                      return (
                        <div
                          key={`${day}-${slot}`}
                          className={`console-g-cell ${state ? `s-${state}` : ""}`.trim()}
                          title={
                            runs.length > 0
                              ? runs.map((r) => r.name).filter((n) => n != null && n !== "").join(", ")
                              : ""
                          }
                        >
                          {runs.length > 0 && (
                            <span className="console-cell-badge">
                              {runs.length}
                            </span>
                          )}
                          {runs.length > 0 ? (
                            runs.slice(0, 2).map((r, idx) => {
                              const runId = r.id ?? r._id;
                              return (
                                <div key={runId ?? r.name ?? idx} className="console-cell-entry">
                                  {runId ? (
                                    <Link
                                      to={getConsoleRunUrl(cityId, typeof runId === "string" ? runId : String(runId), meetingsRange)}
                                      className="ce-club console-cell-run-link"
                                      title="View run"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {r.name}
                                    </Link>
                                  ) : (
                                    <div className="ce-club">{r.name}</div>
                                  )}
                                </div>
                              );
                            })
                          ) : null}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="console-gaps-panel">
              <SectionLabel label="Gaps to Fill" tip="Empty and fragile slots ranked by priority. Fill these to improve city coverage." />
              <div className="console-gap-list">
                {Array.isArray(data.gaps) && data.gaps.length > 0
                  ? data.gaps.map((g, i) => (
                      <GapRow key={i} day={g.day} slot={g.slot} state={g.state} />
                    ))
                  : (
                      <div className="console-gap-empty-msg">All slots covered.</div>
                    )}
              </div>
            </div>
          </div>

          <div className="console-trav-band">
            <SectionLabel label="Traveler Week" tip="Simulates a visitor finding a run each day. ✓ = AM and PM covered, ~ = partial, ✗ = nothing scheduled." />
            <div className="console-trav-sub">
              Can a visitor find a run every day?
            </div>
            <div className="console-trav-row">
              {data.traveler_week && data.traveler_week.length > 0
                ? data.traveler_week.map((d, i) => (
                    <div
                      key={i}
                      className={`console-trav-day ${d.status}`}
                      title={
                        d.status === "ok"
                          ? "AM and PM covered"
                          : d.status === "warn"
                            ? "Partial coverage"
                            : "No runs"
                      }
                    >
                      <span className="trav-day-name">{d.day}</span>
                      <span className="trav-day-status">
                        {d.status === "ok"
                          ? "✓"
                          : d.status === "warn"
                            ? "~"
                            : "✗"}
                      </span>
                    </div>
                  ))
                : null}
            </div>
          </div>

          {/* Stability — always show */}
          <SectionLabel label="Stability" />
          <div className="console-stability-badge">
            {typeof data.stability_forecast?.empty_count === "number" && data.stability_forecast.empty_count > 0 && (
              <span>{data.stability_forecast.empty_count} empty</span>
            )}
            {typeof data.stability_forecast?.fragile_count === "number" && data.stability_forecast.fragile_count > 0 && (
              <span>{data.stability_forecast.fragile_count} fragile</span>
            )}
            {data.stability_forecast?.empty_count === 0 && data.stability_forecast?.fragile_count === 0 && (
              <span>All slots stable</span>
            )}
          </div>

          {/* Region Density — always show */}
          <SectionLabel label="Region Density" tip="Distribution of runs across regions. Prevents over-concentration in one neighborhood." />
          <div className="console-density-list">
            {Array.isArray(data.density) && data.density.length > 0
              ? data.density.map((row, i) => (
                  <div key={i} className="console-density-row">
                    <span className="console-density-lbl">{row.region_label}</span>
                    <span className="console-density-n">{row.count}</span>
                  </div>
                ))
              : (
                  <div className="console-density-row console-ops-empty">No region data yet.</div>
                )}
          </div>

          {/* Fragile slots workspace — always show (v18: "Held only by [club]") */}
          <OpsCard kicker="Fragile slots workspace">
            <OpsList
              items={
                Array.isArray(data.fragile_slots) && data.fragile_slots.length > 0
                  ? data.fragile_slots.map((s) => ({
                      title: `${s.day} ${s.slot != null ? String(s.slot).toUpperCase() : ""}`,
                      description: `${s.club_name ? `Held only by ${s.club_name}. ` : ""}1 run — add a second for stability`,
                    }))
                  : []
              }
              emptyMessage="No fragile slots. City has backup where it matters."
            />
          </OpsCard>

          {/* Club concentration by club (v18) — always show */}
          <OpsCard kicker="Club concentration" tip="Which clubs hold most runs. High concentration in one club is a fragility risk.">
            <OpsList
              items={
                Array.isArray(data.club_concentration) && data.club_concentration.length > 0
                  ? data.club_concentration.map((c) => ({
                      title: `${c.club_name != null ? c.club_name : c.club_id}${c.pct != null ? ` ${c.pct}%` : c.run_count != null ? ` ${c.run_count} runs` : ""}`,
                      description: c.run_count != null ? `${c.run_count} runs shape city perception.` : (c.count != null ? `${c.count} clubs` : ""),
                    }))
                  : []
              }
              emptyMessage="No concentration data yet."
            />
          </OpsCard>

          {/* Doctrine reminder — always 3 rows (v18) */}
          <OpsCard kicker="Doctrine reminder">
            <OpsList items={DOCTRINE_ROWS} emptyMessage="" />
          </OpsCard>

          {/* Anchor Risk Monitor — always show */}
          <SectionLabel label="Anchor Risk Monitor" tip="Anchor runs that are the sole run in their slot. If that run stops, the slot goes empty." />
          <OpsList
            items={
              Array.isArray(data.anchor_risk_list) && data.anchor_risk_list.length > 0
                ? data.anchor_risk_list.map((r) => ({
                    title: `${r.day} ${r.slot != null ? String(r.slot).toUpperCase() : ""} — ${r.run_name != null ? r.run_name : ""}`,
                    description: r.risk,
                  }))
                : []
            }
            emptyMessage="No sole-anchor slots. Good."
          />

        </div>
      )}
    </ConsoleViewShell>
  );
}
